import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";

import { AppShell } from "./AppShell";
import { CallDetailPane } from "./Inbound";
import { API_URL, BOT_URL, suppressNavOnSelection } from "./lib/api";

const PHONE_STORAGE_KEY = "scenarios:phone";
const EXPLAINER_DISMISS_KEY = "scenarios:explainerDismissed";

// ---------- types ----------------------------------------------------------
type CustomerOption = {
  id: number;
  phone: string;
  first_name: string | null;
  last_name: string | null;
  plan: string;
  churn_risk: number;
  notes: string | null;
};

type CallSummary = {
  id: number;
  direction: "inbound" | "outbound" | "copilot";
  from_number: string;
  to_number: string;
  started_at: string;
  ended_at: string | null;
  outcome: string | null;
  customer_id: number | null;
  first_name: string | null;
  last_name: string | null;
  customer_phone: string | null;
};

// ---------- main page ------------------------------------------------------
export function TestAgent({ callId }: { callId?: number }) {
  const [calls, setCalls] = useState<CallSummary[]>([]);
  const [, setLocation] = useLocation();

  // Stream the test-call list (is_test=1).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/calls?limit=100&is_test=1`);
        const d = (await r.json()) as { calls: CallSummary[] };
        if (!cancelled) setCalls(d.calls);
      } catch {
        /* SSE will catch up */
      }
    })();
    const es = new EventSource(`${API_URL}/api/calls/stream?is_test=1`);
    es.addEventListener("calls", (ev) => {
      const d = JSON.parse((ev as MessageEvent).data) as { calls: CallSummary[] };
      setCalls(d.calls);
    });
    return () => {
      cancelled = true;
      es.close();
    };
  }, []);

  // Auto-pick the latest test call when nothing is selected.
  useEffect(() => {
    if (callId !== undefined || calls.length === 0) return;
    const live = calls.find((c) => c.ended_at === null);
    const target = live ?? calls[0];
    if (target) setLocation(`/test-agent/${target.id}`, { replace: true });
  }, [callId, calls, setLocation]);

  return (
    <AppShell>
      <div id="scenarios-test-agent" className="inbound">
        <TestSidebar calls={calls} selectedId={callId} />
        {callId !== undefined ? (
          <CallDetailPane callId={callId} />
        ) : (
          <ScenariosEmpty />
        )}
      </div>
    </AppShell>
  );
}

// First-time visitors see a one-screen explainer of what Scenarios is.
// Once dismissed (or once a run exists), subsequent empty states stay terse.
function ScenariosEmpty() {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(EXPLAINER_DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });
  if (dismissed) {
    return (
      <div id="scenarios-empty" className="inbound-empty">
        Pick a scenario from the panel on the left, or start a new one.
      </div>
    );
  }
  function dismiss() {
    try {
      localStorage.setItem(EXPLAINER_DISMISS_KEY, "1");
    } catch {
      /* private mode etc. */
    }
    setDismissed(true);
  }
  return (
    <div id="scenarios-explainer" className="scenarios-explainer">
      <h2>Reproducible end-to-end runs.</h2>
      <p>
        A subscriber persona dials our number with a fixed profile — the AI
        works the call exactly as it would in production. Every transcript,
        tool call, and advisor card is captured for review.
      </p>
      <ol>
        <li>Enter the phone the test should ring.</li>
        <li>Pick a saved persona from the dropdown.</li>
        <li>Hit <em>Run scenario</em> — your phone rings within a few seconds.</li>
      </ol>
      <button id="scenarios-explainer-dismiss" type="button" className="scenarios-explainer-dismiss" onClick={dismiss}>
        Got it
      </button>
    </div>
  );
}

// ---------- sidebar (form + list) -----------------------------------------
function TestSidebar({
  calls,
  selectedId,
}: {
  calls: CallSummary[];
  selectedId: number | undefined;
}) {
  return (
    <aside id="scenarios-sidebar" className="call-list">
      <header className="pane-header">
        <span>SCENARIOS</span>
      </header>
      <NewTestForm />
      {calls.length === 0 && (
        <div id="scenarios-call-list-empty" className="call-row-empty">No test runs yet.</div>
      )}
      {calls.map((c) => {
        const active = c.ended_at === null;
        const name =
          [c.first_name, c.last_name].filter(Boolean).join(" ") ||
          c.customer_phone ||
          c.to_number;
        return (
          <Link
            key={c.id}
            id={`scenarios-call-row-${c.id}`}
            href={`/scenarios/${c.id}`}
            className={`call-row ${selectedId === c.id ? "is-selected" : ""}`}
            onClick={suppressNavOnSelection}
          >
            <div className="call-row-top">
              <span className={`call-status ${active ? "is-active" : "is-done"}`}>
                {active ? <span className="pulse" /> : null}
                {active ? "Live" : c.outcome ?? "ended"}
              </span>
              <span className="call-direction">test · {c.direction}</span>
            </div>
            <div className="call-row-name">{name}</div>
            <div className="call-row-num">
              {c.from_number} → {c.to_number}
            </div>
            <div className="call-row-time">{relTime(c.started_at)}</div>
          </Link>
        );
      })}
    </aside>
  );
}

// ---------- compact "run new test" form at top of sidebar -----------------
function NewTestForm() {
  // Phone is the same across runs in 99% of cases — persist so the operator
  // doesn't retype it on every reload.
  const [to, setTo] = useState<string>(() => {
    try {
      return localStorage.getItem(PHONE_STORAGE_KEY) ?? "";
    } catch {
      return "";
    }
  });
  const [asCustomerId, setAsCustomerId] = useState("");
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/customers`);
        if (!r.ok) return;
        const d = (await r.json()) as { customers: CustomerOption[] };
        if (!cancelled) setCustomers(d.customers);
      } catch {
        /* dropdown is optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Live preview of the selected persona — surfaces churn / plan / notes so
  // the operator confirms which scenario is about to run before pressing
  // the button.
  const selectedPersona = useMemo(() => {
    if (!asCustomerId) return null;
    return customers.find((c) => String(c.id) === asCustomerId) ?? null;
  }, [asCustomerId, customers]);

  const canSubmit = to.trim().length > 0 && asCustomerId !== "";

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy || !canSubmit) return;
    setBusy(true);
    setErr(null);
    setOkMsg(null);
    try {
      const r = await fetch(`${BOT_URL}/dial`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          to: to.trim(),
          as_customer_id: Number(asCustomerId),
        }),
      });
      const data = await r.json();
      if (!r.ok) {
        setErr(typeof data?.detail === "string" ? data.detail : JSON.stringify(data));
      } else {
        // Persist phone for next time; reset persona to keep the picker honest.
        try {
          localStorage.setItem(PHONE_STORAGE_KEY, to.trim());
        } catch {
          /* private mode */
        }
        setOkMsg("Scenario started — your phone should ring in a few seconds.");
        setAsCustomerId("");
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form id="scenarios-test-form" onSubmit={submit} className="dial-card">
      <label className="dial-field-label" htmlFor="ta-to">
        Your phone number
      </label>
      <input
        id="ta-to"
        type="tel"
        inputMode="tel"
        placeholder="+1XXXXXXXXXX"
        value={to}
        onChange={(e) => {
          setTo(e.target.value);
          setOkMsg(null);
        }}
        required
        disabled={busy}
        className="dial-input"
      />

      <label className="dial-field-label" htmlFor="ta-customer">
        Scenario
      </label>
      <select
        id="ta-customer"
        value={asCustomerId}
        onChange={(e) => {
          setAsCustomerId(e.target.value);
          setOkMsg(null);
        }}
        disabled={busy}
        required
        className="dial-input"
      >
        <option value="">— pick a scenario —</option>
        {customers.map((p) => {
          const name =
            [p.first_name, p.last_name].filter(Boolean).join(" ") || p.phone;
          const churnPct = Math.round(p.churn_risk * 100);
          // Short label only — the persona-preview card below shows
          // plan + notes once a scenario is picked.
          return (
            <option key={p.id} value={p.id}>
              {name} · churn {churnPct}%
            </option>
          );
        })}
      </select>

      {selectedPersona && <PersonaPreview persona={selectedPersona} />}

      <button id="scenarios-dial-submit" type="submit" className="dial-submit" disabled={busy || !canSubmit}>
        {busy ? "Starting…" : (
          <>
            Run scenario <kbd className="kbd kbd-on-accent">Enter</kbd>
          </>
        )}
      </button>
      <Link id="scenarios-manage-link" href="/settings/customers" className="dial-help">
        Manage scenarios in Settings →
      </Link>
      {err && <div id="scenarios-dial-err" className="dial-err">{err}</div>}
      {okMsg && <div id="scenarios-dial-ok" className="dial-ok" role="status">{okMsg}</div>}
    </form>
  );
}

// Compact preview row shown right under the persona dropdown — shows churn,
// plan, and notes so the operator confirms the scenario before running.
function PersonaPreview({ persona }: { persona: CustomerOption }) {
  const churnPct = Math.round(persona.churn_risk * 100);
  const tone = churnPct >= 60 ? "high" : churnPct >= 30 ? "mid" : "low";
  return (
    <div id={`scenarios-persona-preview-${persona.id}`} className="scenario-preview">
      <div className="scenario-preview-row">
        <span className={`scenario-preview-churn tone-${tone}`}>
          Churn {churnPct}%
        </span>
        <span className="scenario-preview-plan">{persona.plan}</span>
      </div>
      {persona.notes && (
        <div className="scenario-preview-notes">{persona.notes}</div>
      )}
    </div>
  );
}

// ---------- helpers --------------------------------------------------------
function relTime(iso: string): string {
  const d = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z");
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return d.toLocaleDateString();
}
